import { LineChart } from '@inmarket/inmarkit';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { Card, CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';

import { useContractManagerContext } from '../../hooks/useContractManagerContext';
import useContractPacingData from '../../hooks/useContractPacingData';
import ContractPacingTable from './ContractPacingTable';

interface ContractPacingDataProps {
  contractId: number;
}

type PacingView = 'chart' | 'table';

const lineChartColors = ['#7DBE42', '#0E3461', '#437EEB', '#5F5F63', '#EBA669', '#E06681'];

function ContractPacingData({ contractId }: ContractPacingDataProps) {
  const { data: json, isLoading, isSuccess } = useContractPacingData(contractId);
  const { selectedLineItemIds } = useContractManagerContext();
  // Line chart is the default view; users can toggle to the pivot table (ADOPS-2694).
  const [view, setView] = useState<PacingView>('chart');

  const data = json?.data || [];

  const dataByLineItemId = data.reduce((accumulator, { insertion_order_id, x, y1, y2 }) => {
    if (!accumulator[insertion_order_id]) {
      accumulator[insertion_order_id] = [];
    }
    accumulator[insertion_order_id].push({
      x,
      y1,
      y2,
    });
    return accumulator;
  }, {});

  // selectedLineItemIds is a MUI GridRowSelectionModel (string|number)[], while
  // Object.keys gives us strings. Normalize to a string Set for membership checks.
  const selectedIdSet = useMemo(() => new Set(selectedLineItemIds.map(String)), [selectedLineItemIds]);

  const allLineItemIds = Object.keys(dataByLineItemId);
  const lineItemIds =
    selectedIdSet.size > 0 ? allLineItemIds.filter((id) => selectedIdSet.has(id)) : allLineItemIds;

  const lineItemPacingData = [];
  for (let i = 0; i < lineItemIds.length; i += 1) {
    const lineItemId = lineItemIds[i];

    const seriesData = dataByLineItemId[lineItemId].map((item) => ({
      x: new Date(item.x),
      goal: Number(item.y1) * 100,
      impression: Number(item.y2) * 100,
    }));

    const turboThreshold = seriesData.length;
    const color = lineChartColors[Number(lineItemId) % lineChartColors.length];

    lineItemPacingData.push({
      name: `Insertion Order ${lineItemId} Goal`,
      turboThreshold,
      data: seriesData.filter((item) => item.goal).map((item) => ({ ...item, y: item.goal })),
      color: '#999',
      // 'Dash' | 'DashDot' | 'Dot' | 'LongDash' | 'LongDashDot' | 'LongDashDotDot' | 'ShortDash' | 'ShortDashDot' | 'ShortDashDotDot' | 'ShortDot' | 'Solid'
      dashStyle: 'Dot',
      tooltip: {
        valueSuffix: '%',
      },
    });

    lineItemPacingData.push({
      name: `Insertion Order ${lineItemId} Impressions`,
      turboThreshold,
      color,
      data: seriesData.filter((item) => item.impression).map((item) => ({ ...item, y: item.impression })),
      lineWidth: 0.75,
      tooltip: {
        valueSuffix: '%',
      },
    });
  }

  const hasData = lineItemPacingData.length > 0;

  const handleViewChange = (_event: React.MouseEvent<HTMLElement>, next: PacingView | null) => {
    // ToggleButtonGroup fires null when the active button is re-clicked; ignore it
    // so a view is always selected.
    if (next) setView(next);
  };

  return (
    <Card sx={{ width: '50%', height: '400px', p: 2, position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={view}
        onChange={handleViewChange}
        aria-label="Pacing by Line Items view"
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'background.paper' }}
      >
        <ToggleButton value="chart" aria-label="Line chart view">
          <ShowChartIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton value="table" aria-label="Table view">
          <TableRowsIcon fontSize="small" />
        </ToggleButton>
      </ToggleButtonGroup>

      {isLoading && <CircularProgress />}
      {isSuccess && !hasData && <Typography>No data</Typography>}
      {isSuccess && hasData && view === 'chart' && (
        <LineChart title="Pacing by Line Items" xAxisType="datetime" data={lineItemPacingData} />
      )}
      {isSuccess && hasData && view === 'table' && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, pr: 10 }}>
            Pacing by Line Items
          </Typography>
          <ContractPacingTable lineItemIds={lineItemIds} dataByLineItemId={dataByLineItemId} />
        </>
      )}
    </Card>
  );
}

export default ContractPacingData;
